import { AuditOperationType } from './auditTrailTypes';

export type AuditActionFilter = 'All' | AuditOperationType;

export interface AuditTrailFilterState {
    search: string;
    action: AuditActionFilter;
    entityTypes: string[];
    authorIds: number[];
    /** Inclusive UTC day bounds as YYYY-MM-DD; the BE widens the upper bound to end of day. */
    changedAtFrom: string | null;
    changedAtTo: string | null;
    /**
     * Drops changes no employee made (deploys, migrations, integrations): the BE stores no author id for them.
     * On by default, so the feed opens on people's changes.
     */
    hideSystem: boolean;
    /** Takes precedence over every other filter — when set, the rest are kept but not applied. */
    transactionId: string | null;
}

export const EMPTY_AUDIT_TRAIL_FILTERS: AuditTrailFilterState = {
    search: '',
    action: 'All',
    entityTypes: [],
    authorIds: [],
    changedAtFrom: null,
    changedAtTo: null,
    hideSystem: true,
    transactionId: null,
};

/** Turns the free-text search into one RSQL predicate; returns '' when it adds no filter. */
export type AuditTrailSearchRsql = (search: string) => string;

function escapeRsqlValue(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/'/g, "\\'");
}

/**
 * Entity type and author have dedicated filters; free-text search targets the record id only,
 * as an exact match — a LIKE would make id 12 surface 112, 120, 3124, … The whole trimmed text
 * is one value, with no keyword splitting or search operators.
 */
export const exactRowIdSearchRsql: AuditTrailSearchRsql = (search) => {
    const value = search.trim();
    return value ? `((rowId=="${escapeRsqlValue(value)}"))` : '';
};

export function buildAuditTrailRsql(
    filters: AuditTrailFilterState,
    searchRsql: AuditTrailSearchRsql = exactRowIdSearchRsql
): string {
    if (filters.transactionId) {
        return `transactionId==${filters.transactionId}`;
    }
    const parts: string[] = [];
    if (filters.action !== 'All') {
        parts.push(`operationType=in=(${filters.action})`);
    }
    if (filters.entityTypes.length > 0) {
        parts.push(`tableName=in=(${filters.entityTypes.join(',')})`);
    }
    if (filters.authorIds.length > 0) {
        parts.push(`authorId=in=(${filters.authorIds.join(',')})`);
    }
    if (filters.hideSystem) {
        parts.push('authorId=nn=');
    }
    if (filters.changedAtFrom) {
        parts.push(`changedAt=ge=${filters.changedAtFrom}`);
    }
    if (filters.changedAtTo) {
        parts.push(`changedAt=le=${filters.changedAtTo}`);
    }
    if (filters.search.trim()) {
        const searchPredicate = searchRsql(filters.search);
        if (searchPredicate) {
            parts.push(searchPredicate);
        }
    }
    return parts.join(';');
}
